import { http } from '@pendo/http';
import memoize from 'lodash/memoize';

export const PROPERTY_ENDPOINT_MAP = {
    throttleSettings: 'throttlesettings',
    extensionDomainPatterns: 'extensionDomainPatterns',
    extensionStructRegExps: 'extensionRegExPatterns',
    taggingClassExclusions: 'taggingClassExclusions',
    taggingIdExclusions: 'taggingIdExclusions',
    sessionReplaySettings: 'sessionReplaySettings',
    tagNamingConvention: 'tagNamingConvention',
};

export function fetchApps ({ includeDeactivated } = {}) {
    const queryParams = includeDeactivated ? '?includeDisabled=true' : '';

    return http.get(`/api/s/_SID_/application${queryParams}`).then((res) => res.data);
}

export function fetchApp (id) {
    return http.get(`/api/s/_SID_/application/${id}`).then((res) => res.data);
}

export function fetchDiscoveryApps () {
    return http.get('/api/s/_SID_/application/discovery/apps').then((res) => res.data);
}

export function fetchDiscoveryCatalogApps () {
    return http.get('/api/s/_SID_/application/discovery/catalogue').then((res) => res.data);
}

export function updateDiscoveryAppDescription (appId, description) {
    return http.put(`/api/s/_SID_/application/discovery/${appId}/description`, { description }).then((res) => res.data);
}

export function updateDiscoveryAppLabels (appId, labels) {
    return http.put(`/api/s/_SID_/application/discovery/${appId}/labels`, { labels }).then((res) => res.data);
}

export function createApp (app) {
    return http.post('/api/s/_SID_/application/', app).then((res) => res.data);
}

export function createCustomDiscoveryApp (payload) {
    return http.post('/api/s/_SID_/application/discovery/custom', payload).then((res) => res.data);
}

export function deactivateApp (id) {
    return http.delete(`/api/s/_SID_/application/${id}`);
}

export function updateApp (app) {
    return http.put(`/api/s/_SID_/application/${app.id}`, app).then((res) => res.data);
}

export function updateAppFlag (id, flag, enabled) {
    return http.put(`/api/s/_SID_/application/${id}/flag/${flag}`, { set: enabled }).then((res) => res.data);
}

export function testJwksUri (id, jwksUri) {
    return http
        .put(`/api/s/_SID_/application/${id}/jwksuri/validation`, jwksUri, {
            headers: { 'Content-Type': 'text/plain' },
        })
        .then((res) => res.data);
}

export function saveJwksUri (id, jwksUri) {
    return http
        .put(`/api/s/_SID_/application/${id}/jwksuri`, jwksUri, {
            headers: { 'Content-Type': 'text/plain' },
        })
        .then((res) => res.data);
}

export function deleteJwksUri (id) {
    return http.delete(`/api/s/_SID_/application/${id}/jwksuri`).then((res) => res.data);
}

export function updateDomains (id, urls) {
    return http.post(`/api/s/_SID_/application/${id}/domainAccess/bulk`, { urls }).then((res) => res.data);
}

// the same app ID should always return the same key, no need to run this more than once for a given app ID
export const getMobileAppKey = memoize((id) => {
    return http.get(`/api/s/_SID_/application/${id}/mobileAppKey`).then((res) => res.data);
});

export function updateAppProperty (id, propertyName, propertyValue) {
    const endpoint = getEndpointForProperty(propertyName);
    const formattedPropertyValue = formatValueForWebRequest(propertyName, propertyValue);
    if (!endpoint) {
        return Promise.reject(new Error(`${propertyName} is not setup for individual PUT.`));
    }

    return http
        .put(`/api/s/_SID_/application/${id}/${endpoint}`, formattedPropertyValue)
        .then((res) => formatAppPropertyResponseValue(propertyName, res.data));
}

export function updateDiscoveryAppLicenses (discoveryAppId, licenses) {
    return http.put(`/api/s/_SID_/application/discovery/${discoveryAppId}/licenses`, { licenses }).then((res) => res.data);
}

export function updateDiscoveryAppCost (appId, cost) {
    return http.put(`/api/s/_SID_/application/discovery/${appId}/cost`, { cost }).then((res) => res.data);
}

export function updateDiscoveryAppOwner (appId, ownedByUserId) {
    return http.put(`/api/s/_SID_/application/discovery/${appId}/owner`, { ownedByUserId }).then((res) => res.data);
}

export function updateDiscoveryAppRenewalDate (appId, renewalDate) {
    return http.put(`/api/s/_SID_/application/discovery/${appId}/renewaldate`, { renewalDate }).then((res) => res.data);
}

// As part of R+P Permissions, app properties will migrate to a separate API, but not all at once.
export function getEndpointForProperty (propertyName) {
    return PROPERTY_ENDPOINT_MAP[propertyName];
}

export function formatAppPropertyResponseValue (propertyName, response) {
    switch (propertyName) {
        case 'taggingIdExclusions':
        case 'taggingClassExclusions':
        case 'extensionDomainPatterns':
        case 'extensionStructRegExps':
        case 'tagNamingConvention':

            return response[propertyName];
        default:
            return response;
    }
}

export function formatValueForWebRequest (propertyName, propertyValue) {
    switch (propertyName) {
        case 'taggingIdExclusions':
        case 'taggingClassExclusions':
        case 'extensionDomainPatterns':
        case 'extensionStructRegExps':
        case 'tagNamingConvention':
            return { [propertyName]: propertyValue };
        case 'sessionReplaySettings':
            return { maskedSelectors: [], blockedSelectors: [], unmaskedSelectors: [], ...propertyValue };
        default:
            return propertyValue;
    }
}
